/**
 * PIEMonthInReviewCard — surfaces a "Voice month-in-review" growth
 * artifact at the top of /mirror/dashboard.
 *
 * Lifecycle
 * ─────────
 * 1. On mount, calls GET /coach/month-in-review/current. If
 *    `eligible=false` (fewer than 3 saved reflections in last 30
 *    days, OR the user already saved one for this period), the card
 *    returns null — no clutter for new users.
 * 2. If eligible, renders an opt-in CTA: "Look back at the past 30
 *    days, in 3 quotes."
 * 3. On click, POST /generate fetches the LLM essay (Claude weaves
 *    the 3 most resonant reflections into a 150-220 word narrative).
 * 4. The user can replay the narrative via TTS, save it as a
 *    permanent artifact, or dismiss the draft.
 *
 * Save persists the essay to pie_month_reviews.saved=true so the
 * dashboard suppresses the card for the same period afterward.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, BookMarked, Volume2, Square, X, Loader2, ChevronRight, CheckCircle2 } from 'lucide-react';
import CoachAvatar from '../../../components/coach/CoachAvatar';
import useCoachVoice from '../../../hooks/useCoachVoice';
import authedFetch from '../../../lib/authedFetch';
import { pieApi } from '../pieApi';
import { toast } from 'sonner';
import logger from '../../../lib/logger';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PIEMonthInReviewCard() {
  // Phases: 'loading' | 'hidden' | 'cta' | 'generating' | 'draft' | 'saved'
  const [phase, setPhase] = useState('loading');
  const [meta, setMeta] = useState(null); // /current response
  const [draft, setDraft] = useState(null); // /generate response
  const [coachPrefs, setCoachPrefs] = useState(null);
  const [persona, setPersona] = useState(null);
  const { speak, cancel, speaking, supported: voiceSupported } = useCoachVoice();
  // Track ttsId so we can show a Stop affordance during playback.
  const [playing, setPlaying] = useState(false);
  const cleanupRef = useRef(false);

  // Wire speech engine state back to local "playing" so the button
  // flips between Replay ↔ Stop.
  useEffect(() => {
    if (!speaking && playing) setPlaying(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speaking]);

  // Initial eligibility check + persona/prefs lookup. We resolve all
  // three in parallel so the card can render in one paint.
  useEffect(() => {
    cleanupRef.current = false;
    Promise.all([
      pieApi.monthInReviewCurrent().catch((e) => {
        logger.warn('month-in-review current failed', e);
        return { eligible: false };
      }),
      authedFetch(`${API}/api/coach/preferences`).then((r) => r.ok ? r.json() : null).catch(() => null),
      authedFetch(`${API}/api/coach/personas`).then((r) => r.ok ? r.json() : { personas: [] }).catch(() => ({ personas: [] })),
    ]).then(([cur, prefs, catalog]) => {
      if (cleanupRef.current) return;
      setCoachPrefs(prefs);
      const pid = prefs?.persona_id;
      setPersona((catalog.personas || []).find((p) => p.id === pid) || null);
      setMeta(cur);
      setPhase(cur?.eligible ? 'cta' : 'hidden');
    });
    return () => { cleanupRef.current = true; };
  }, []);

  const onGenerate = async () => {
    setPhase('generating');
    try {
      const d = await pieApi.generateMonthInReview();
      setDraft(d);
      setPhase('draft');
      // Auto-narrate when voice is on. Mirrors the in-coach
      // ReflectionSummaryCard behavior so the experience is
      // consistent across surfaces.
      if (voiceSupported && !coachPrefs?.muted && d?.narrative) {
        setPlaying(true);
        speak(d.narrative, {
          voiceUri: coachPrefs?.voice_uri,
          rate: coachPrefs?.voice_rate || 1.0,
          muted: false,
        });
      }
    } catch (e) {
      logger.warn('month-in-review generate failed', e);
      toast.error("Couldn't generate the review just now. Try again in a moment.");
      setPhase('cta');
    }
  };

  const onReplayToggle = () => {
    if (!draft?.narrative) return;
    if (playing) {
      cancel();
      setPlaying(false);
      return;
    }
    cancel();
    setPlaying(true);
    speak(draft.narrative, {
      voiceUri: coachPrefs?.voice_uri,
      rate: coachPrefs?.voice_rate || 1.0,
      muted: false,
    });
  };

  const onSave = async () => {
    if (!draft) return;
    try {
      const r = await pieApi.saveMonthInReview(draft.id);
      setDraft({ ...draft, ...r });
      setPhase('saved');
      toast.success('Saved as a growth artifact');
      if (speaking) cancel();
    } catch (e) {
      logger.warn('save month-in-review failed', e);
      toast.error("Couldn't save just now. Try again.");
    }
  };

  const onDismiss = () => {
    if (speaking) cancel();
    setPhase('hidden');
  };

  if (phase === 'loading' || phase === 'hidden') return null;

  return (
    <section
      className="pie-card pie-month-review"
      data-testid="pie-month-review"
      aria-label={`${meta?.period_label || ''} month in review`}
    >
      <button
        type="button"
        className="pie-month-review-close"
        onClick={onDismiss}
        data-testid="pie-month-review-dismiss"
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>

      <div className="pie-month-review-eyebrow">
        <Sparkles size={12} />
        <span>Month in review · {meta?.period_label || ''}</span>
      </div>

      {phase === 'cta' && (
        <>
          <h3 className="pie-month-review-title pie-month-review-title-cta">
            Look back at your past 30 days — in 3 quotes.
          </h3>
          <p className="pie-month-review-sub">
            You&apos;ve saved {meta?.reflection_count || 0} voice reflections this month.
            Your coach can thread the most resonant ones into a short essay you
            can keep as a growth artifact.
          </p>
          <button
            type="button"
            className="pie-btn pie-btn-primary"
            onClick={onGenerate}
            data-testid="pie-month-review-generate"
          >
            See your month in review <ChevronRight size={13} />
          </button>
        </>
      )}

      {phase === 'generating' && (
        <div className="pie-month-review-loading" data-testid="pie-month-review-loading">
          <Loader2 size={16} className="pie-month-review-spinner" />
          <span>Threading your reflections into a narrative…</span>
        </div>
      )}

      {(phase === 'draft' || phase === 'saved') && draft && (
        <>
          <h3 className="pie-month-review-title" data-testid="pie-month-review-title">
            {draft.title}
          </h3>
          <div className="pie-month-review-row">
            {persona && <CoachAvatar persona={persona} size={48} />}
            <p
              className="pie-month-review-narrative"
              data-testid="pie-month-review-narrative"
            >
              {draft.narrative}
            </p>
          </div>

          {Array.isArray(draft.picked_reflections) && draft.picked_reflections.length > 0 && (
            <details className="pie-month-review-quotes" data-testid="pie-month-review-quotes">
              <summary>The 3 reflections threaded above</summary>
              <ul>
                {draft.picked_reflections.map((q, i) => (
                  <li key={q.id || i}>
                    <span className="pie-month-review-quote-date">
                      {q.created_at ? new Date(q.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}
                    </span>
                    <span className="pie-month-review-quote-text">&ldquo;{q.summary}&rdquo;</span>
                  </li>
                ))}
              </ul>
            </details>
          )}

          <div className="pie-month-review-actions">
            {voiceSupported && !coachPrefs?.muted && (
              <button
                type="button"
                className="pie-coach-toolbar-btn"
                onClick={onReplayToggle}
                data-testid="pie-month-review-replay"
                aria-pressed={playing}
              >
                {playing
                  ? <><Square size={12} /> Stop</>
                  : <><Volume2 size={12} /> Replay</>}
              </button>
            )}
            {phase === 'saved'
              ? (
                <button
                  type="button"
                  className="pie-btn pie-btn-primary"
                  disabled
                  data-testid="pie-month-review-saved"
                >
                  <CheckCircle2 size={13} /> Saved
                </button>
              )
              : (
                <button
                  type="button"
                  className="pie-btn pie-btn-primary"
                  onClick={onSave}
                  data-testid="pie-month-review-save"
                >
                  <BookMarked size={13} /> Keep as growth artifact
                </button>
              )}
          </div>
        </>
      )}
    </section>
  );
}
